import React, { useState, useEffect, useRef } from "react";
import {
  FiChevronDown,
  FiArrowUp,
  FiArrowDown,
  FiBookOpen,
  FiUsers,
  FiHome,
  FiHeart,
  FiShield,
  FiActivity,
  FiStar,
  FiChevronUp,
  FiChevronRight,
  FiGlobe,
  FiCalendar,
  FiDownload,
  FiShare2,
  FiClock,
  FiHelpCircle,
  FiMinus,
  FiTrendingUp,
  FiAlertTriangle,
  FiX
} from "react-icons/fi";
import API_BASE_URL from "../../config/api";
import { useAuth } from "../../context/AuthContext";
import { getZoneColor, getScoreZone } from "../../utils/zoneUtils";

// Utility Icons for Categories
const CategoryIcons: Record<string, React.FC<any>> = {
  "Academics": FiBookOpen,
  "Teachers": FiUsers,
  "Friends": FiUsers,
  "Family": FiHome,
  "Emotional Wellbeing": FiHeart,
  "Safety": FiShield,
  "Physical Wellbeing": FiActivity,
  "Life Skills": FiStar,
  "Digital Wellbeing": FiGlobe,
  "School Environment": FiHome,
};

// getZoneColor and getScoreZone are imported from ../../utils/zoneUtils — single source of truth

const getScoreColor = (score: number): string => getZoneColor(getScoreZone(score)).color;
const getBarColorClass = (score: number) => {
  const zone = getScoreZone(score);
  switch (zone) {
    case "Green Zone": return "bg-emerald-500";
    case "Blue Zone": return "bg-blue-500";
    case "Yellow Zone": return "bg-yellow-500";
    case "Brown Zone": return "bg-amber-700";
    case "Red Zone": return "bg-red-500";
    case "Dark Red": return "bg-red-800";
    default: return "bg-slate-300";
  }
};
const getScoreLabel = (score: number) => {
  const zone = getScoreZone(score);
  switch (zone) {
    case "Green Zone": return "Thriving";
    case "Blue Zone": return "Stable";
    case "Yellow Zone": return "Watch";
    case "Brown Zone": return "Risk";
    case "Red Zone": return "High Risk";
    case "Dark Red": return "Critical";
    default: return "Unknown";
  }
};
const getScoreBadgeClass = (score: number) => {
  const zone = getScoreZone(score);
  switch (zone) {
    case "Green Zone": return "bg-emerald-50 text-emerald-700 border-emerald-200";
    case "Blue Zone": return "bg-blue-50 text-blue-700 border-blue-200";
    case "Yellow Zone": return "bg-yellow-50 text-yellow-700 border-yellow-200";
    case "Brown Zone": return "bg-amber-50 text-amber-800 border-amber-200";
    case "Red Zone": return "bg-red-50 text-red-700 border-red-200";
    case "Dark Red": return "bg-red-100 text-red-900 border-red-300";
    default: return "bg-slate-50 text-slate-700 border-slate-200";
  }
};

const CustomSelect = ({ label, value, options, onChange }: any) => {
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const h = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setIsOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);
  const selected = options.find((o: any) => o.value === value) || options[0];
  return (
    <div className="relative flex items-center gap-2" ref={ref}>
      {label && <span className="text-xs font-semibold text-slate-500">{label}</span>}
      <div
        className="flex items-center justify-between min-w-[140px] px-3 py-2 bg-white border border-slate-200 rounded-lg text-xs font-bold text-slate-700 cursor-pointer shadow-sm hover:border-slate-300 transition-colors"
        onClick={() => setIsOpen(!isOpen)}
      >
        <span className="truncate pr-2">{selected?.label}</span>
        <FiChevronDown size={14} className={`text-slate-400 transition-transform ${isOpen ? "rotate-180" : ""}`} />
      </div>
      {isOpen && (
        <div className="absolute top-full right-0 mt-1 w-full min-w-[140px] bg-white border border-slate-100 rounded-xl shadow-xl py-1.5 z-50">
          {options.map((opt: any) => (
            <div key={opt.value}
              onClick={() => { onChange(opt.value); setIsOpen(false); }}
              className={`px-3 py-2.5 text-xs font-bold cursor-pointer transition-colors ${value === opt.value ? "bg-slate-50 text-slate-900" : "text-slate-600 hover:bg-slate-50"}`}
            >{opt.label}</div>
          ))}
        </div>
      )}
    </div>
  );
};

// Deviation Chart for Level 3 and 4
const DeviationChart = ({ score, avg }: { score: number, avg: number }) => {
  const diff = score - avg;
  const isPositive = diff > 0;
  const maxDiff = 30; // Scale max for the bar length
  const widthPct = Math.min(Math.abs((diff / maxDiff) * 100), 100);
  
  return (
    <div className="flex items-center w-32 relative h-4">
      <div className="absolute left-1/2 top-0 bottom-0 w-px bg-slate-300 border-r border-dashed border-slate-400"></div>
      <div className="w-1/2 flex justify-end pr-1">
        {!isPositive && diff !== 0 && (
          <div className="h-1.5 rounded-l-sm bg-red-500" style={{ width: `${widthPct}%` }}></div>
        )}
      </div>
      <div className="w-1/2 pl-1 flex justify-start">
        {isPositive && (
          <div className="h-1.5 rounded-r-sm bg-emerald-500" style={{ width: `${widthPct}%` }}></div>
        )}
      </div>
    </div>
  );
};


const Benchmarking: React.FC = () => {
  const { user } = useAuth();
  const schoolId = user?.schoolId || (user?.userType === 'school' ? user?.id : undefined);

  // States
  const [boardData, setBoardData] = useState<any[]>([]);
  const [gradeData, setGradeData] = useState<any[]>([]);
  const [classData, setClassData] = useState<any[]>([]);

  const [selectedBoard, setSelectedBoard] = useState("all");
  const [selectedGrade, setSelectedGrade] = useState("all");
  
  const [selectedCategoryPopup, setSelectedCategoryPopup] = useState<string | null>(null);
  const [showInsightsModal, setShowInsightsModal] = useState(false);
  const [compareLevel, setCompareLevel] = useState<"grade" | "class">("grade");
  const [selectedPeriod, setSelectedPeriod] = useState<string>("all_time");
  
  const [loading, setLoading] = useState(true);

  // Fetch Board and External data
  useEffect(() => {
    const fetchInitial = async () => {
      setLoading(true);
      try {
        const q = new URLSearchParams({ period: selectedPeriod });
        if (schoolId) q.append("schoolId", schoolId.toString());
        
        const boardRes = await window.fetch(`${API_BASE_URL}/schools/analytics/benchmarking?type=internal&compareBy=board&${q}`);
        const boardJson = await boardRes.json();
        
        const validBoardData = Array.isArray(boardJson) ? boardJson : [];
        setBoardData(validBoardData);

        if (validBoardData.length > 0) {
          const firstBoard = validBoardData[0].groupName;
          setSelectedBoard(firstBoard);
        }
      } catch (e) { console.error(e); }
      setLoading(false);
    };
    fetchInitial();
  }, [schoolId, selectedPeriod]);

  // Fetch Grade data
  useEffect(() => {
    if (!selectedBoard || selectedBoard === "all") {
      setGradeData([]);
      return;
    }
    const fetchGrades = async () => {
      try {
        const q = new URLSearchParams({ period: selectedPeriod, compareBy: "grade", board: selectedBoard });
        if (schoolId) q.append("schoolId", schoolId.toString());
        const res = await window.fetch(`${API_BASE_URL}/schools/analytics/benchmarking?type=internal&${q}`);
        const json = await res.json();
        const validGrades = Array.isArray(json) ? json : [];
        setGradeData(validGrades);
        
        if (validGrades.length > 0) {
          const firstGradeStr = validGrades[0].groupName;
          const match = firstGradeStr.match(/\d+/);
          setSelectedGrade(match ? match[0] : firstGradeStr);
        } else {
          setSelectedGrade("all");
        }
      } catch(e) { console.error(e); }
    };
    fetchGrades();
  }, [selectedBoard, schoolId, selectedPeriod]);

  // Fetch Class data
  useEffect(() => {
    if (!selectedGrade || selectedGrade === "all") {
      setClassData([]);
      return;
    }
    const fetchClasses = async () => {
      try {
        const q = new URLSearchParams({ period: selectedPeriod, compareBy: "class", grade: selectedGrade, board: selectedBoard });
        if (schoolId) q.append("schoolId", schoolId.toString());
        const res = await window.fetch(`${API_BASE_URL}/schools/analytics/benchmarking?type=internal&${q}`);
        const json = await res.json();
        const validClasses = Array.isArray(json) ? json : [];
        setClassData(validClasses);
      } catch(e) { console.error(e); }
    };
    fetchClasses();
  }, [selectedGrade, selectedBoard, schoolId, selectedPeriod]);

  const allCategoriesSet = new Set<string>();
  boardData.forEach(b => b.categories?.forEach((c:any) => allCategoriesSet.add(c.category)));
  const categoriesList = allCategoriesSet.size > 0 ? Array.from(allCategoriesSet) : [
    "Academics", "Friends / Peers", "Family", "Emotional Wellbeing", "Safety", "Physical Wellbeing", "Life Skills"
  ];

  const aiInsights = React.useMemo(() => {
    const insights = [];
    
    // Board level insights
    const boardObj = boardData.find(b => b.groupName === selectedBoard) || boardData[0];
    if (boardObj && boardObj.categories) {
      boardObj.categories.forEach((cat: any) => {
        if (cat.score >= 75) {
          insights.push({
            type: "positive",
            icon: FiTrendingUp,
            color: "text-emerald-500",
            text: `${cat.category} wellbeing is a key strength for ${boardObj.groupName} with an excellent score of ${cat.score}.`
          });
        } else if (cat.score <= 60) {
          insights.push({
            type: "negative",
            icon: FiAlertTriangle,
            color: "text-red-500",
            text: `${cat.category} wellbeing is critically low (${cat.score}) for ${boardObj.groupName}. Requires immediate attention.`
          });
        }
      });
    }

    // Grade level insight
    if (gradeData && gradeData.length > 0) {
      gradeData.forEach(grade => {
        if (grade.zones && grade.zones.atRiskPct >= 10) {
          insights.push({
            type: "warning",
            icon: FiAlertTriangle,
            color: "text-orange-500",
            text: `${grade.groupName} shows higher % at risk (${grade.zones.atRiskPct}%). Focus on early interventions.`
          });
        }
      });
    }

    if (insights.length === 0) {
      insights.push({
        type: "neutral",
        icon: FiStar,
        color: "text-indigo-500",
        text: "Gathering enough data to generate deep AI Insights..."
      });
    }

    return insights;
  }, [boardData, gradeData, selectedBoard]);

  if (loading) {
    return <div className="flex justify-center items-center h-screen"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600" /></div>;
  }

  const boardOptions = boardData.map(b => ({ value: b.groupName, label: b.groupName }));
  if (boardOptions.length === 0) boardOptions.push({ value: "all", label: "No Boards" });

  const gradeOptions = gradeData.map(g => {
    const match = g.groupName.match(/\d+/);
    const val = match ? match[0] : g.groupName;
    return { value: val, label: `Grade ${val}` };
  });
  if (gradeOptions.length === 0) gradeOptions.push({ value: "all", label: "No Grades" });

  const selectedGradeObj = gradeData.find(g => {
    const m = g.groupName.match(/\d+/);
    return (m ? m[0] : g.groupName) === selectedGrade;
  });
  const selectedGradeOverallScore = selectedGradeObj?.overallScore || 0;
  const selectedGradeStudentCount = selectedGradeObj?.zones?.studentCount || 0;
  
  const totalStudentsOverall = boardData.reduce((acc, curr) => acc + (curr.zones?.studentCount || 0), 0);

  return (
    <div className="animate-fadeIn min-h-screen bg-slate-50 text-slate-800 font-sans pb-20">
      
      {/* Top Header */}
      <div className="bg-white border-b border-slate-200 px-6 py-5 flex items-center justify-between sticky top-0 z-40">
        <div className="flex items-center gap-4">
          <div className="w-10 h-10 rounded-xl bg-teal-50 flex items-center justify-center text-teal-600 shadow-sm border border-teal-100">
            <FiActivity size={20} />
          </div>
          <div>
            <h1 className="text-xl font-black text-slate-900 tracking-tight">Benchmarking & Comparisons</h1>
            <p className="text-xs text-slate-500 mt-0.5">
              {compareLevel === 'grade'
                ? <>Comparing <strong className="text-teal-600">each grade's wellbeing</strong> against the <strong className="text-teal-600">{selectedBoard} school average</strong></>
                : <>Comparing <strong className="text-teal-600">each class in Grade {selectedGrade}</strong> against the <strong className="text-teal-600">Grade {selectedGrade} average</strong></>}
            </p>
          </div>
        </div>
        <div className="flex gap-4">
           <div className="flex items-center gap-3 bg-slate-50 border border-slate-200 px-4 py-2 rounded-xl">
              <FiCalendar className="text-slate-400" />
              <div>
                <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">Last Updated</p>
                <p className="text-xs font-bold text-slate-700">Today, 09:30 AM</p>
              </div>
           </div>
           <div className="flex items-center gap-3 bg-slate-50 border border-slate-200 px-4 py-2 rounded-xl">
              <FiUsers className="text-slate-400" />
              <div>
                <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">Total Students</p>
                <p className="text-xs font-bold text-slate-700">{totalStudentsOverall.toLocaleString()}</p>
              </div>
           </div>
        </div>
      </div>

      <div className="px-6 py-6 max-w-[1600px] mx-auto">
          <div className="flex gap-8 relative items-start">
            


            {/* Center Content */}
            <div className="flex-1 space-y-8 min-w-0">

              {/* Filters */}
              <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 flex flex-wrap items-center gap-4 justify-between">
                {/* Left: Compare By Toggle */}
                <div className="flex items-center gap-3">
                  <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">Compare By</span>
                  <div className="flex bg-slate-100 p-1 rounded-lg">
                    <button 
                      onClick={() => setCompareLevel("grade")}
                      className={`px-4 py-1.5 rounded-md text-xs font-bold transition-all ${compareLevel === "grade" ? "bg-white text-teal-600 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}
                    >
                      Grade
                    </button>
                    <button 
                      onClick={() => setCompareLevel("class")}
                      className={`px-4 py-1.5 rounded-md text-xs font-bold transition-all ${compareLevel === "class" ? "bg-white text-teal-600 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}
                    >
                      Class
                    </button>
                  </div>
                  {/* Helper hint */}
                  <span className="text-[11px] text-slate-400">
                    {compareLevel === 'grade' 
                      ? <>Each grade compared vs <strong>school avg ({selectedBoard})</strong></> 
                      : <>Each class compared vs <strong>Grade {selectedGrade} avg</strong></>}
                  </span>
                </div>

                {/* Divider */}
                <div className="h-8 w-px bg-slate-200 hidden sm:block" />

                {/* Right: Context-aware Filters */}
                <div className="flex flex-wrap items-center gap-3 ml-auto">
                  <CustomSelect 
                    label="Period" 
                    value={selectedPeriod} 
                    options={[
                      { value: "current_month", label: "Current Month" },
                      { value: "last_month", label: "Last Month" },
                      { value: "last_quarter", label: "Last Quarter" },
                      { value: "year", label: "Year to Date" },
                      { value: "all_time", label: "All Time" }
                    ]} 
                    onChange={setSelectedPeriod} 
                  />
                  <CustomSelect label="Board" value={selectedBoard} options={boardOptions} onChange={setSelectedBoard} />
                  {/* Grade filter only shown in Class mode */}
                  {compareLevel === "class" && (
                    <CustomSelect label="Grade" value={selectedGrade} options={gradeOptions} onChange={setSelectedGrade} />
                  )}
                </div>
              </div>

              {/* Class Mode: Breadcrumb Context Banner */}
              {compareLevel === 'class' && (
                <div className="bg-teal-50 border border-teal-100 rounded-xl px-5 py-3 flex items-center gap-2 text-sm">
                  <FiChevronRight size={14} className="text-teal-400" />
                  <span className="text-teal-700 font-medium">
                    Showing classes in <strong>{selectedBoard}</strong> → <strong>Grade {selectedGrade}</strong>
                  </span>
                  {classData.length > 0 && (
                    <span className="ml-auto text-teal-600 font-bold text-xs">{classData.length} classes found</span>
                  )}
                  {classData.length === 0 && (
                    <span className="ml-auto text-orange-500 font-bold text-xs">No class data — select a grade above</span>
                  )}
                </div>
              )}
              
              {/* Level 1: Board Level Summary — hidden in Class mode (irrelevant at class level) */}
              {compareLevel === 'grade' && (
              <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
                 <div className="flex justify-between items-start mb-6">
                   <div>
                     <h3 className="text-base font-black text-slate-800">Level 1: Board Level Summary</h3>
                     <p className="text-xs text-slate-500 mt-1">Overview of wellbeing performance across all educational boards.</p>
                   </div>
                   <div className="text-[10px] text-slate-400 font-bold uppercase flex items-center gap-1">
                     % At Risk (Red + Dark Red) <FiHelpCircle size={12}/>
                   </div>
                 </div>

                 <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
                   {boardData.map(board => (
                     <div key={board.groupName} className="border border-slate-200 rounded-2xl p-5 hover:border-slate-300 transition-colors shadow-sm bg-slate-50/30">
                       <div className="flex items-center gap-3 mb-6">
                         <div className="w-10 h-10 rounded-xl bg-white border border-slate-100 flex items-center justify-center text-indigo-500 shadow-sm">
                           <FiBookOpen size={18} />
                         </div>
                         <div>
                           <h4 className="text-sm font-black text-slate-800">{board.groupName}</h4>
                           <p className="text-[11px] text-slate-500 font-medium">{(board.zones?.studentCount || 0).toLocaleString()} Students</p>
                         </div>
                       </div>
                       
                       <div className="flex flex-col items-center justify-center mb-6">
                          <span className="text-2xl font-black text-red-600">{board.zones?.atRiskPct || 0}%</span>
                          <span className="text-[10px] text-slate-500 font-bold uppercase mt-1">At Risk</span>
                          <div className="w-full h-1.5 bg-slate-200 rounded-full mt-3 overflow-hidden">
                            <div className="h-full bg-red-500" style={{width: `${board.zones?.atRiskPct || 0}%`}}></div>
                          </div>
                       </div>

                       <div className="pt-4 border-t border-slate-200/60 flex items-center justify-between">
                         <div>
                           <p className="text-[10px] text-slate-500 font-bold uppercase">Avg. Score</p>
                           <p className="text-lg font-black text-slate-800">{board.overallScore}</p>
                         </div>
                         <div className={`px-2.5 py-1 rounded-full text-[10px] font-bold border flex items-center gap-1.5 ${getScoreBadgeClass(board.overallScore)}`}>
                           <div className="w-1.5 h-1.5 rounded-full" style={{backgroundColor: getScoreColor(board.overallScore)}}></div>
                           {getScoreLabel(board.overallScore)}
                         </div>
                       </div>
                     </div>
                   ))}
                 </div>
                 </div>
              )}

              {/* Level 2: Grade Zone Distribution */}
              <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
                 <div className="flex justify-between items-center mb-6">
                   <div>
                     <h3 className="text-base font-black text-slate-800">
                       {compareLevel === 'class' ? 'Level 2: Zone Distribution by Class' : 'Level 2: Zone Distribution by Grade'}
                     </h3>
                     <p className="text-xs text-slate-500 mt-1">
                       {compareLevel === 'class' 
                         ? `Distribution of students across wellbeing zones for each class in Grade ${selectedGrade}.` 
                         : `Distribution of students across wellbeing zones for each grade in ${selectedBoard}.`}
                     </p>
                   </div>
                 </div>

                 {/* Top Legend */}
                 <div className="flex items-center justify-between mb-6 px-4 py-3 bg-slate-50 rounded-xl border border-slate-100">
                    {["Green Zone", "Blue Zone", "Yellow Zone", "Brown Zone", "Red Zone", "Dark Red"].map(z => {
                      let label = z;
                      let range = "";
                      if(z==="Green Zone"){label="Thriving"; range="86-100";}
                      if(z==="Blue Zone"){label="Stable"; range="71-85";}
                      if(z==="Yellow Zone"){label="Watch"; range="43-70";}
                      if(z==="Brown Zone"){label="Risk"; range="29-42";}
                      if(z==="Red Zone"){label="High Risk"; range="14-28";}
                      if(z==="Dark Red"){label="Critical"; range="0-13";}
                      return (
                        <div key={z} className="flex flex-col items-center">
                          <div className="flex items-center gap-1.5">
                            <div className="w-2.5 h-2.5 rounded-full" style={{backgroundColor: getZoneColor(z).color}}></div>
                            <span className="text-[11px] font-bold text-slate-700">{label}</span>
                          </div>
                          <span className="text-[9px] text-slate-400 mt-0.5">{range}</span>
                        </div>
                      )
                    })}
                 </div>

                 <div className="overflow-x-auto">
                   <table className="w-full text-left">
                     <thead>
                       <tr className="border-b border-slate-200">
                         <th className="pb-3 text-xs font-bold text-slate-800 w-20">{compareLevel === 'class' ? 'Class' : 'Grade'}</th>
                         <th className="pb-3 text-xs font-bold text-slate-800 w-24">Students</th>
                         <th className="pb-3 text-xs font-bold text-slate-800"></th>
                         <th className="pb-3 text-[10px] font-bold text-slate-500 uppercase text-right w-24">% At Risk</th>
                       </tr>
                     </thead>
                     <tbody className="divide-y divide-slate-100">
                       {(compareLevel === 'class' ? classData : gradeData).map((grade) => {
                          const z = grade.zones || {};
                          return (
                            <tr key={grade.groupName} className="hover:bg-slate-50/50 transition-colors">
                              <td className="py-4 text-xs font-bold text-slate-700">{grade.groupName}</td>
                              <td className="py-4 text-xs font-medium text-slate-500">{(z.studentCount || 0).toLocaleString()}</td>
                              <td className="py-4 pr-6">
                                {/* Stacked Bar */}
                                <div className="h-6 rounded-md overflow-hidden flex cursor-pointer group/bar relative">
                                  {z["Green Zone"] > 0 && <div className="h-full bg-emerald-500 transition-all flex items-center justify-center text-[10px] text-white font-bold" style={{width: `${z["Green Zone"]}%`}}>{z["Green Zone"] > 5 ? `${z["Green Zone"]}%` : ''}</div>}
                                  {z["Blue Zone"] > 0 && <div className="h-full bg-blue-500 transition-all flex items-center justify-center text-[10px] text-white font-bold" style={{width: `${z["Blue Zone"]}%`}}>{z["Blue Zone"] > 5 ? `${z["Blue Zone"]}%` : ''}</div>}
                                  {z["Yellow Zone"] > 0 && <div className="h-full bg-yellow-500 transition-all flex items-center justify-center text-[10px] text-white font-bold" style={{width: `${z["Yellow Zone"]}%`}}>{z["Yellow Zone"] > 5 ? `${z["Yellow Zone"]}%` : ''}</div>}
                                  {z["Brown Zone"] > 0 && <div className="h-full bg-amber-700 transition-all flex items-center justify-center text-[10px] text-white font-bold" style={{width: `${z["Brown Zone"]}%`}}>{z["Brown Zone"] > 5 ? `${z["Brown Zone"]}%` : ''}</div>}
                                  {z["Red Zone"] > 0 && <div className="h-full bg-red-500 transition-all flex items-center justify-center text-[10px] text-white font-bold" style={{width: `${z["Red Zone"]}%`}}>{z["Red Zone"] > 5 ? `${z["Red Zone"]}%` : ''}</div>}
                                  {z["Dark Red"] > 0 && <div className="h-full bg-red-800 transition-all flex items-center justify-center text-[10px] text-white font-bold" style={{width: `${z["Dark Red"]}%`}}>{z["Dark Red"] > 5 ? `${z["Dark Red"]}%` : ''}</div>}
                                  
                                  {/* Tooltip */}
                                  <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-48 bg-white border border-slate-200 shadow-xl rounded-xl p-3 z-50 opacity-0 invisible group-hover/bar:opacity-100 group-hover/bar:visible transition-all">
                                    <div className="space-y-1.5">
                                      <div className="flex justify-between text-[10px] font-bold"><span className="text-emerald-700">Thriving (86-100)</span><span>{z["Green Zone"] || 0}%</span></div>
                                      <div className="flex justify-between text-[10px] font-bold"><span className="text-blue-700">Stable (71-85)</span><span>{z["Blue Zone"] || 0}%</span></div>
                                      <div className="flex justify-between text-[10px] font-bold"><span className="text-yellow-700">Watch (43-70)</span><span>{z["Yellow Zone"] || 0}%</span></div>
                                      <div className="flex justify-between text-[10px] font-bold"><span className="text-amber-800">Risk (29-42)</span><span>{z["Brown Zone"] || 0}%</span></div>
                                      <div className="flex justify-between text-[10px] font-bold"><span className="text-red-600">High Risk (14-28)</span><span>{z["Red Zone"] || 0}%</span></div>
                                      <div className="flex justify-between text-[10px] font-bold"><span className="text-red-900">Critical (0-13)</span><span>{z["Dark Red"] || 0}%</span></div>
                                    </div>
                                  </div>
                                </div>
                              </td>
                              <td className="py-4 text-right">
                                <span className="text-xs font-black text-red-600">{z.atRiskPct}%</span>
                              </td>
                            </tr>
                          )
                       })}
                     </tbody>
                   </table>
                 </div>
              </div>

              {/* Level 3: Category Comparisons */}
              <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
                 <div className="flex justify-between items-start mb-6">
                   <div>
                     <h3 className="text-base font-black text-slate-800">
                       {compareLevel === 'class' ? 'Level 3: Category Comparisons (Classes)' : 'Level 3: Category Comparisons (Grades)'}
                     </h3>
                     <p className="text-xs text-slate-500 mt-1">
                       {compareLevel === 'class' 
                         ? 'Compare classes against grade average for each wellbeing category.' 
                         : 'Compare grades against school average for each wellbeing category.'}
                     </p>
                   </div>
                 </div>

                 <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                   {categoriesList.map(cat => {
                     const Icon = CategoryIcons[cat] || FiActivity;
                     const boardObj = boardData.find(b => b.groupName === selectedBoard);
                     const schoolAvg = boardObj?.categories?.find((c:any) => c.category === cat)?.score || 0;
                     
                     const gradeObj = gradeData.find(g => {
                       const m = g.groupName.match(/\d+/);
                       return (m ? m[0] : g.groupName) === selectedGrade;
                     });
                     const gradeAvg = gradeObj?.categories?.find((c:any) => c.category === cat)?.score || 0;
                     
                     const avgToCompare = compareLevel === 'class' ? gradeAvg : schoolAvg;

                     return (
                       <div key={cat} className="col-span-1 border border-slate-200 hover:border-slate-300 rounded-xl transition-all duration-300 bg-white">
                         <div 
                           className="p-4 flex items-center justify-between cursor-pointer rounded-xl hover:bg-slate-50 transition-colors"
                           onClick={() => setSelectedCategoryPopup(cat)}
                         >
                           <div className="flex items-center gap-3">
                             <div className="w-8 h-8 rounded-full flex items-center justify-center shadow-sm bg-slate-50 text-slate-500 border border-slate-100">
                               <Icon size={14} />
                             </div>
                             <span className="text-sm font-bold text-slate-800">{cat}</span>
                           </div>
                           <div className="flex items-center gap-4">
                             <div className="text-right">
                               <p className="text-[10px] text-slate-400 font-bold uppercase">{compareLevel === 'class' ? 'Grade Avg.' : 'School Avg.'}</p>
                               <p className="text-sm font-black text-slate-800">{avgToCompare}</p>
                             </div>
                             <div className="p-1.5 rounded-lg text-slate-400 group-hover:text-slate-600 transition-colors">
                               <FiChevronDown />
                             </div>
                           </div>
                         </div>
                       </div>
                     )
                   })}
                 </div>
              </div>

              {/* Level 4: Class-wise Deep Dive */}
              {compareLevel === 'grade' && (
              <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
                 <div className="flex justify-between items-start mb-6 pb-4 border-b border-slate-100">
                   <div>
                     <h3 className="text-base font-black text-slate-800">Level 4: Class-wise Deep Dive</h3>
                     <p className="text-xs text-slate-500 mt-1">Overall wellbeing scores for classes in selected grade.</p>
                   </div>
                 </div>

                 <div className="flex flex-col xl:flex-row gap-8">
                   
                   {/* Left side average block */}
                   <div className="w-full xl:w-56 shrink-0 bg-slate-50 border border-slate-200 rounded-2xl p-6 flex flex-col items-center justify-center text-center">
                     <p className="text-xs font-bold text-slate-600 mb-1">Grade {selectedGrade} Average</p>
                     <span className="text-5xl font-black text-slate-800 my-4" style={{ color: getScoreColor(selectedGradeOverallScore) }}>
                        {selectedGradeOverallScore}
                     </span>
                     <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-bold border mb-6 ${getScoreBadgeClass(selectedGradeOverallScore)}`}>
                        <div className="w-1.5 h-1.5 rounded-full" style={{backgroundColor: getScoreColor(selectedGradeOverallScore)}}></div>
                        {getScoreLabel(selectedGradeOverallScore)}
                     </span>
                     <div className="flex items-center gap-2 text-slate-500">
                       <FiUsers size={16} />
                       <span className="text-xs font-bold">{selectedGradeStudentCount.toLocaleString()} Students</span>
                     </div>
                   </div>

                   {/* Right side table */}
                   <div className="flex-1 overflow-x-auto">
                     <table className="w-full text-left min-w-[500px]">
                       <thead>
                         <tr className="border-b border-slate-100">
                           <th className="pb-2 text-[10px] font-bold text-slate-500 uppercase w-16">Class</th>
                           <th className="pb-2 text-[10px] font-bold text-slate-500 uppercase w-16">Students</th>
                           <th className="pb-2 text-[10px] font-bold text-slate-500 uppercase text-center w-40">Overall Score</th>
                           <th className="pb-2 text-[10px] font-bold text-slate-500 uppercase text-center w-24">Zone</th>
                           <th className="pb-2 text-[10px] font-bold text-slate-500 uppercase text-center">vs Grade Avg ({selectedGradeOverallScore})</th>
                           <th className="pb-2 text-[10px] font-bold text-slate-500 uppercase text-right w-20">Difference</th>
                         </tr>
                       </thead>
                       <tbody className="divide-y divide-slate-50">
                         {classData.map(cls => {
                           const diff = cls.overallScore - selectedGradeOverallScore;
                           return (
                             <tr key={cls.groupName} className="hover:bg-slate-50/30 transition-colors">
                               <td className="py-3 text-xs font-black text-slate-800">{cls.groupName}</td>
                               <td className="py-3 text-xs font-medium text-slate-500">{cls.zones?.studentCount || 0}</td>
                               <td className="py-3 px-2">
                                 <div className="flex items-center gap-2">
                                   <span className="text-xs font-black w-6 text-slate-700">{cls.overallScore}</span>
                                   <div className="flex-1 bg-slate-100 h-2.5 rounded-full overflow-hidden">
                                      <div className={`h-full ${getBarColorClass(cls.overallScore)}`} style={{width: `${(cls.overallScore/100)*100}%`}}></div>
                                   </div>
                                 </div>
                               </td>
                               <td className="py-3 text-center">
                                 <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[9px] font-bold border ${getScoreBadgeClass(cls.overallScore)}`}>
                                   <div className="w-1 h-1 rounded-full" style={{backgroundColor: getScoreColor(cls.overallScore)}}></div>
                                   {getScoreLabel(cls.overallScore)}
                                 </span>
                               </td>
                               <td className="py-3 text-center">
                                  <div className="flex justify-center w-full">
                                    <DeviationChart score={cls.overallScore} avg={selectedGradeOverallScore} />
                                  </div>
                               </td>
                               <td className="py-3 text-right">
                                 <span className={`inline-flex items-center gap-0.5 text-xs font-bold ${diff > 0 ? 'text-emerald-600' : diff < 0 ? 'text-red-500' : 'text-slate-400'}`}>
                                   {diff > 0 ? '+' : ''}{diff} {diff > 0 ? <FiArrowUp size={10}/> : diff < 0 ? <FiArrowDown size={10}/> : <FiMinus size={10}/>}
                                 </span>
                               </td>
                             </tr>
                           )
                         })}
                       </tbody>
                     </table>
                     {classData.length === 0 && <div className="text-center py-8 text-sm text-slate-400">No class data available.</div>}
                   </div>
                 </div>
                 
                 <div className="mt-6 pt-4 border-t border-slate-100">
                    <p className="text-[10px] text-slate-400 font-medium">Note: % At Risk includes students in High Risk (15-28) and Critical (0-14) zones.</p>
                 </div>
              </div>
              )}

            </div>
            
            {/* Category Popup Modal */}
            {selectedCategoryPopup && (
              <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 pl-[260px] bg-slate-900/40 backdrop-blur-sm animate-fadeIn">
                <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col animate-scaleUp">
                  
                  {/* Modal Header */}
                  <div className="px-6 py-5 border-b border-slate-100 flex items-center justify-between bg-slate-50">
                    <div className="flex items-center gap-3">
                      {(() => {
                        const Icon = CategoryIcons[selectedCategoryPopup] || FiActivity;
                        return (
                          <div className="w-10 h-10 rounded-full flex items-center justify-center shadow-sm bg-white text-teal-600 border border-slate-200">
                            <Icon size={18} />
                          </div>
                        );
                      })()}
                      <div>
                        <h3 className="text-lg font-black text-slate-800">{selectedCategoryPopup}</h3>
                        <p className="text-xs text-slate-500">{compareLevel === 'class' ? 'Class comparison vs Grade Average' : 'Grade comparison vs School Average'}</p>
                      </div>
                    </div>
                    <button 
                      onClick={() => setSelectedCategoryPopup(null)}
                      className="p-2 rounded-full hover:bg-slate-200 text-slate-400 hover:text-slate-700 transition-colors"
                    >
                      <FiX size={20} />
                    </button>
                  </div>

                  {/* Modal Body */}
                  <div className="p-6 overflow-y-auto overflow-x-auto min-h-0 flex-1">
                    {(() => {
                       const boardObj = boardData.find(b => b.groupName === selectedBoard);
                       const schoolAvg = boardObj?.categories?.find((c:any) => c.category === selectedCategoryPopup)?.score || 0;
                       const gradeObj = gradeData.find(g => {
                         const m = g.groupName.match(/\d+/);
                         return (m ? m[0] : g.groupName) === selectedGrade;
                       });
                       const gradeAvg = gradeObj?.categories?.find((c:any) => c.category === selectedCategoryPopup)?.score || 0;
                       const avgToCompare = compareLevel === 'class' ? gradeAvg : schoolAvg;
                       const dataList = compareLevel === 'class' ? classData : gradeData;
                       
                       return (
                          <table className="w-full text-left min-w-[620px]">
                            <thead>
                              <tr className="border-b border-slate-100">
                                <th className="pb-3 text-xs font-bold text-slate-500 uppercase w-24 pr-2">{compareLevel === 'class' ? 'Class' : 'Grade'}</th>
                                <th className="pb-3 text-xs font-bold text-slate-500 uppercase text-center w-44">Score</th>
                                <th className="pb-3 text-xs font-bold text-slate-500 uppercase text-center w-28">Zone</th>
                                <th className="pb-3 text-xs font-bold text-slate-500 uppercase text-center w-36">vs {compareLevel === 'class' ? 'Grade Avg' : 'School Avg'} ({avgToCompare})</th>
                                <th className="pb-3 text-xs font-bold text-slate-500 uppercase text-right w-24">Difference</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-50">
                              {dataList.map(grade => {
                                const gradeScore = grade.categories?.find((c:any) => c.category === selectedCategoryPopup)?.score || 0;
                                const diff = gradeScore - avgToCompare;
                                return (
                                  <tr key={grade.groupName} className="hover:bg-slate-50/50 transition-colors">
                                    <td className="py-4 text-sm font-bold text-slate-800 pr-2">{grade.groupName}</td>
                                    <td className="py-4 px-3">
                                      <div className="flex items-center gap-3">
                                        <span className="text-sm font-black w-8 text-slate-700 shrink-0">{gradeScore}</span>
                                        <div className="flex-1 bg-slate-100 h-2.5 rounded-full overflow-hidden">
                                           <div className={`h-full ${getBarColorClass(gradeScore)}`} style={{width: `${(gradeScore/100)*100}%`}}></div>
                                        </div>
                                      </div>
                                    </td>
                                    <td className="py-4 text-center">
                                      <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-bold border ${getScoreBadgeClass(gradeScore)}`}>
                                        <div className="w-1.5 h-1.5 rounded-full" style={{backgroundColor: getScoreColor(gradeScore)}}></div>
                                        {getScoreLabel(gradeScore)}
                                      </span>
                                    </td>
                                    <td className="py-4 text-center">
                                       <div className="flex justify-center w-full">
                                         <DeviationChart score={gradeScore} avg={avgToCompare} />
                                       </div>
                                    </td>
                                    <td className="py-4 text-right">
                                      <span className={`inline-flex items-center gap-1 text-sm font-bold ${diff > 0 ? 'text-emerald-600' : diff < 0 ? 'text-red-500' : 'text-slate-400'}`}>
                                        {diff > 0 ? '+' : ''}{diff} {diff > 0 ? <FiArrowUp size={12}/> : diff < 0 ? <FiArrowDown size={12}/> : <FiMinus size={12}/>}
                                      </span>
                                    </td>
                                  </tr>
                                )
                              })}
                            </tbody>
                          </table>
                        );
                    })()}
                  </div>
                </div>
              </div>
            )}

            {/* AI Insights Modal */}
            {showInsightsModal && (
              <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 pl-[260px] bg-slate-900/40 backdrop-blur-sm animate-fadeIn">
                <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] overflow-hidden flex flex-col animate-scaleUp">
                  <div className="px-6 py-5 border-b border-slate-100 flex items-center justify-between bg-slate-50">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full flex items-center justify-center shadow-sm bg-white text-indigo-500 border border-indigo-100">
                        <FiStar size={18} />
                      </div>
                      <div>
                        <h3 className="text-lg font-black text-slate-800">All AI Insights</h3>
                        <p className="text-xs text-slate-500">Comprehensive analysis of your wellbeing data</p>
                      </div>
                    </div>
                    <button 
                      onClick={() => setShowInsightsModal(false)}
                      className="p-2 rounded-full hover:bg-slate-200 text-slate-400 hover:text-slate-700 transition-colors"
                    >
                      <FiX size={20} />
                    </button>
                  </div>
                  <div className="p-6 overflow-y-auto space-y-4">
                    {aiInsights.map((insight, idx) => {
                      const Icon = insight.icon;
                      return (
                        <div key={idx} className="flex gap-4 p-4 rounded-xl border border-slate-100 bg-slate-50/50">
                          <div className={`mt-0.5 p-2 rounded-full bg-white shadow-sm border border-slate-100 ${insight.color}`}>
                            <Icon className="w-4 h-4" />
                          </div>
                          <div>
                            <p className="text-sm font-bold text-slate-800 mb-1">{insight.type === 'positive' ? 'Strength Identified' : insight.type === 'warning' ? 'Area for Attention' : 'Critical Issue'}</p>
                            <p className="text-xs text-slate-600 leading-relaxed">{insight.text}</p>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </div>
              </div>
            )}

            {/* Right Sidebar (Sticky) */}
            <div className="hidden 2xl:block w-72 shrink-0 space-y-6 sticky top-32">
              
              {/* Zone Guide Card */}
              <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5">
                 <h4 className="text-sm font-black text-slate-800 mb-4">Wellbeing Zone Guide</h4>
                 <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-8 rounded-lg bg-emerald-500"></div>
                      <div className="leading-tight"><p className="text-[11px] font-black text-slate-700">86 - 100</p><p className="text-[10px] text-slate-500 font-medium">Thriving</p></div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-8 rounded-lg bg-blue-500"></div>
                      <div className="leading-tight"><p className="text-[11px] font-black text-slate-700">72 - 85</p><p className="text-[10px] text-slate-500 font-medium">Stable</p></div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-8 rounded-lg bg-yellow-500"></div>
                      <div className="leading-tight"><p className="text-[11px] font-black text-slate-700">58 - 71</p><p className="text-[10px] text-slate-500 font-medium">Watch</p></div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-8 rounded-lg bg-orange-500"></div>
                      <div className="leading-tight"><p className="text-[11px] font-black text-slate-700">43 - 57</p><p className="text-[10px] text-slate-500 font-medium">Warning</p></div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-8 rounded-lg bg-purple-500"></div>
                      <div className="leading-tight"><p className="text-[11px] font-black text-slate-700">29 - 42</p><p className="text-[10px] text-slate-500 font-medium">Risk</p></div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-8 rounded-lg bg-red-500"></div>
                      <div className="leading-tight"><p className="text-[11px] font-black text-slate-700">15 - 28</p><p className="text-[10px] text-slate-500 font-medium">High Risk</p></div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-8 rounded-lg bg-red-900"></div>
                      <div className="leading-tight"><p className="text-[11px] font-black text-slate-700">0 - 14</p><p className="text-[10px] text-slate-500 font-medium">Critical</p></div>
                    </div>
                 </div>
              </div>

              {/* AI Insights Card */}
              <div className="bg-gradient-to-b from-indigo-50/50 to-white rounded-2xl border border-indigo-100 shadow-sm p-5">
                 <h4 className="text-sm font-black text-slate-800 mb-4 flex items-center gap-2">
                   <FiStar className="text-indigo-500 w-4 h-4" /> AI Insights
                 </h4>
                 <div className="space-y-4">
                   {aiInsights.slice(0, 2).map((insight, idx) => {
                     const Icon = insight.icon;
                     return (
                       <div key={idx} className={`flex gap-3 ${idx > 0 ? 'pt-3 border-t border-indigo-50' : ''}`}>
                         <div className="mt-0.5"><Icon className={`w-4 h-4 ${insight.color}`} /></div>
                         <p className="text-[11px] text-slate-600 leading-snug">{insight.text}</p>
                       </div>
                     )
                   })}
                 </div>
                 {aiInsights.length > 2 && (
                   <button 
                     onClick={() => setShowInsightsModal(true)}
                     className="w-full mt-4 py-2 border border-indigo-200 rounded-lg text-xs font-bold text-indigo-600 bg-white hover:bg-indigo-50 transition-colors"
                   >
                     View All Insights ({aiInsights.length})
                   </button>
                 )}
              </div>

            </div>
          </div>
      </div>
    </div>
  );
};

export default Benchmarking;
